export function isNil(value: any) {
  return value === null || value === undefined;
}

const invalidCpfs = [
  '00000000000',
  '11111111111',
  '22222222222',
  '33333333333',
  '44444444444',
  '55555555555',
  '66666666666',
  '77777777777',
  '88888888888',
  '99999999999',
];
const invalidCnpjs = [
  '00000000000000',
  '11111111111111',
  '22222222222222',
  '33333333333333',
  '44444444444444',
  '55555555555555',
  '66666666666666',
  '77777777777777',
  '88888888888888',
  '99999999999999',
];

/**
 * Remove caracteres de formatação do CNPJ/CPF (pontos, barra, traço, espaços),
 * mantendo letras e dígitos para suportar o CNPJ alfanumérico.
 */
export function stripCpfCnpjFormatting(val: string): string {
  return val?.replace(/[.\-\/\s]+/g, '').toUpperCase() ?? '';
}

/**
 * Obtém o valor numérico de um caractere para cálculo do dígito verificador do CNPJ alfanumérico.
 * Dígitos: valor numérico (0-9). Letras: código ASCII - 48 (A=17, B=18, ..., Z=42).
 */
function getCharValue(char: string): number {
  const code = char.charCodeAt(0);
  if (code >= 65 && code <= 90) return code - 48;
  return code - 48;
}

/**
 * Verifica se o CNPJ contém caracteres alfabéticos (novo formato alfanumérico).
 */
function isCnpjAlfanumerico(val: string): boolean {
  return /[A-Z]/i.test(val.substring(0, 12));
}

/**
 * Valida CNPJ alfanumérico usando módulo 11 com pesos [5,4,3,2,9,8,7,6,5,4,3,2]
 * conforme definido pela Receita Federal para o novo formato.
 * As 12 primeiras posições podem ser alfanuméricas (A-Z, 0-9) e as 2 últimas são dígitos verificadores numéricos.
 */
function validaCnpjAlfanumerico(val: string): boolean {
  if (val.length !== 14) return false;

  const base = val.substring(0, 12).toUpperCase();
  const digitosInformados = val.substring(12);

  if (!/^\d{2}$/.test(digitosInformados)) return false;
  if (!/^[A-Z0-9]{12}$/.test(base)) return false;
  if (/^(.)\1{13}$/.test(val)) return false;

  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  let soma = 0;
  for (let i = 0; i < 12; i++) {
    soma += getCharValue(base[i]) * pesos1[i];
  }
  let resto = soma % 11;
  const digito1 = resto < 2 ? 0 : 11 - resto;

  if (digito1 !== +digitosInformados[0]) return false;

  const baseComDigito1 = base + digito1.toString();
  soma = 0;
  for (let i = 0; i < 13; i++) {
    soma += getCharValue(baseComDigito1[i]) * pesos2[i];
  }
  resto = soma % 11;
  const digito2 = resto < 2 ? 0 : 11 - resto;

  if (digito2 !== +digitosInformados[1]) return false;

  return true;
}

export function validaCpfCnpj(val: string): boolean {
  if (!val) return false;

  const limpo = stripCpfCnpjFormatting(val);

  if (!limpo) return false;

  if (limpo.length === 11) {
    const cpfDigits = limpo.replace(/\D/g, '');
    if (cpfDigits.length !== 11) return false;

    if (invalidCpfs.some((cpf) => cpf === cpfDigits)) return false;

    let soma = 0;
    let resto;
    for (let i = 1; i <= 9; i++) {
      soma = soma + +cpfDigits.substring(i - 1, i) * (11 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) {
      resto = 0;
    }
    if (resto !== +cpfDigits.substring(9, 10)) {
      return false;
    }
    soma = 0;
    for (let i = 1; i <= 10; i++) {
      soma = soma + +cpfDigits.substring(i - 1, i) * (12 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) {
      resto = 0;
    }
    if (resto !== +cpfDigits.substring(10, 11)) {
      return false;
    }
    return true;
  }

  if (limpo.length !== 14) return false;

  if (isCnpjAlfanumerico(limpo)) {
    return validaCnpjAlfanumerico(limpo);
  }

  if (invalidCnpjs.some((cnpj) => cnpj === limpo)) return false;

  return validaCnpjAlfanumerico(limpo);
}

export function validaCpf(val: string): boolean {
  val = val.replace(/[\D]+/g, '');

  if (val === '') return false;

  let soma = 0;
  if (val.length === 11) {
    if (invalidCpfs.some((cpf) => cpf === val)) return false;

    let resto;
    for (let i = 1; i <= 9; i++) soma = soma + +val.substring(i - 1, i) * (11 - i);

    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;

    if (resto !== +val.substring(9, 10)) return false;

    soma = 0;
    for (let i = 1; i <= 10; i++) soma = soma + +val.substring(i - 1, i) * (12 - i);

    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;

    if (resto !== +val.substring(10, 11)) return false;

    return true;
  } else return false;
}

export function validaEmail(email: string): boolean {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export function validaCep(cep: string): boolean {
  const re = /^[0-9]{8}$/;
  return re.test(String(cep).toLowerCase().replace(/\D/g, ''));
}

export function validaContaDeposito(contaDeposito: string): boolean {
  const re = /^[0-9]{12}$/;

  return re.test(String(contaDeposito).toLowerCase().replace(/\D/g, ''));
}
